/**
 * Team skill matrix — rows = staff, columns = skills, cell = latest level.
 * Shows the skill index per person, who can train whom, and where the team
 * has gaps. HR can maintain the skill catalogue from here.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Grid3x3, Loader2, Plus, Settings2, ChevronRight, TrendingUp, TrendingDown, Trash2,
  GraduationCap, Mail, AlertTriangle, ClipboardList,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr, inputCls } from '../../../components/admin/billing/shared';
import { LevelChip, SkillLegend, indexTone } from './shared';

const SkillMatrixPage = () => {
  const { authHeader } = useAuth();
  const [data, setData] = useState({ catalogue: [], rows: [], levels: [], coverage: [] });
  const [meta, setMeta] = useState({ roles: [], groups: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [showCatalogue, setShowCatalogue] = useState(false);
  const [newSkill, setNewSkill] = useState({ group: '', name: '', applicable_roles: [] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: m }, { data: mx }] = await Promise.all([
        axios.get(`${API}/skills/meta`, { headers: authHeader() }),
        axios.get(`${API}/skills/matrix`, { headers: authHeader() }),
      ]);
      setMeta(m);
      setData(mx);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const addSkill = async () => {
    if (!newSkill.group.trim() || !newSkill.name.trim()) { toast.error('Group and skill name required'); return; }
    setBusy('add');
    try {
      await axios.post(`${API}/skills/catalogue`, newSkill, { headers: authHeader() });
      toast.success('Skill added');
      setNewSkill({ group: '', name: '', applicable_roles: [] });
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const renameSkill = async (sk, name) => {
    if (!name || name === sk.name) return;
    try {
      await axios.patch(`${API}/skills/catalogue/${sk.id}`, { name }, { headers: authHeader() });
      toast.success('Renamed');
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const retireSkill = async (sk) => {
    if (!window.confirm(`Retire "${sk.name}"? Past assessments keep their ratings.`)) return;
    try {
      await axios.delete(`${API}/skills/catalogue/${sk.id}`, { headers: authHeader() });
      toast.success('Skill retired');
      await load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const sendReminders = async () => {
    setBusy('reminders');
    try {
      const { data: r } = await axios.post(`${API}/skills/reminders/send`, {}, { headers: authHeader() });
      if (r.skipped) toast.info(`Nothing to nudge: ${r.reason}`);
      else if (r.error) toast.error(`${r.error} · ${r.stale_count} overdue`);
      else toast.success(`${r.sent} nudge(s) sent for ${r.stale_count} overdue assessment(s)`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const groups = [...new Set(data.catalogue.map((s) => s.group))];
  const staleCount = data.rows.filter((r) => r.stale).length;

  return (
    <div className="space-y-5 pb-20" data-testid="skill-matrix-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Grid3x3 size={20} className="text-teal-600" /> Skill Matrix
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Latest assessed level per skill. The skill index feeds 30% of each appraisal score.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/admin/skills/training"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50"
            data-testid="training-plan-link"
          >
            <GraduationCap size={15} /> Training plan
          </Link>
          <Link
            to="/admin/skills/duty-templates"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50"
            data-testid="duty-templates-link"
          >
            <ClipboardList size={15} /> Duty templates
          </Link>
          <Link
            to="/admin/skills/catalogue"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50"
            data-testid="skill-catalogue-link"
          >
            <Grid3x3 size={15} /> Edit skills
          </Link>
          {data.is_hr && (
            <>
              <button
                onClick={sendReminders} disabled={busy === 'reminders'}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                data-testid="skill-reminders-btn"
              >
                {busy === 'reminders' ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />}
                Nudge overdue
              </button>
              <button
                onClick={() => setShowCatalogue((v) => !v)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50"
                data-testid="toggle-catalogue-btn"
              >
                <Settings2 size={15} /> {showCatalogue ? 'Hide' : 'Edit'} skill list
              </button>
            </>
          )}
        </div>
      </div>

      {!!staleCount && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800 flex items-center gap-2" data-testid="stale-banner">
          <AlertTriangle size={15} />
          {staleCount} team member(s) haven&apos;t had a signed skill assessment in over 90 days.
        </div>
      )}

      {showCatalogue && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="catalogue-editor">
          <div className="grid gap-2 sm:grid-cols-12">
            <input
              className={`${inputCls} sm:col-span-3`} placeholder="Group (e.g. Ceramic)"
              list="skill-groups" value={newSkill.group}
              onChange={(e) => setNewSkill({ ...newSkill, group: e.target.value })}
              data-testid="new-skill-group"
            />
            <datalist id="skill-groups">
              {[...new Set([...(meta.groups || []), ...groups])].map((g) => <option key={g} value={g} />)}
            </datalist>
            <input
              className={`${inputCls} sm:col-span-5`} placeholder="Skill name"
              value={newSkill.name}
              onChange={(e) => setNewSkill({ ...newSkill, name: e.target.value })}
              data-testid="new-skill-name"
            />
            <select
              multiple
              className={`${inputCls} sm:col-span-3 h-[38px]`}
              value={newSkill.applicable_roles}
              onChange={(e) => setNewSkill({
                ...newSkill,
                applicable_roles: Array.from(e.target.selectedOptions).map((o) => o.value),
              })}
              data-testid="new-skill-roles"
            >
              {(meta.roles || []).map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <button
              onClick={addSkill} disabled={busy === 'add'}
              className="sm:col-span-1 px-3 py-2 rounded-lg bg-teal-600 text-white text-sm hover:bg-teal-700 disabled:opacity-50"
              data-testid="add-skill-btn"
            >
              {busy === 'add' ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            </button>
          </div>
          <p className="text-[11px] text-slate-400">
            Leave roles empty to apply the skill to everyone. Select roles to build a role-specific set.
          </p>
          <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
            {data.catalogue.map((sk) => (
              <div key={sk.id} className="flex items-center gap-2 py-1.5" data-testid={`cat-row-${sk.id}`}>
                <span className="text-xs text-slate-400 w-32 shrink-0 truncate">{sk.group}</span>
                <input
                  defaultValue={sk.name}
                  onBlur={(e) => renameSkill(sk, e.target.value.trim())}
                  className={`${inputCls} !py-1 flex-1`}
                  data-testid={`cat-name-${sk.id}`}
                />
                <span className="text-[11px] text-slate-400 w-40 truncate">
                  {(sk.applicable_roles || []).join(', ') || 'all roles'}
                </span>
                <button
                  onClick={() => retireSkill(sk)}
                  className="p-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                  data-testid={`cat-retire-${sk.id}`}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <SkillLegend levels={data.levels || []} />

      {loading ? (
        <div className="p-12 flex justify-center text-slate-400"><Loader2 className="animate-spin" /></div>
      ) : !data.rows.length ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-sm text-slate-500" data-testid="no-matrix">
          No active staff to map yet.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <table className="text-sm w-full" data-testid="matrix-table">
            <thead>
              <tr className="bg-slate-50">
                <th className="text-left px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 sticky left-0 bg-slate-50 z-10 min-w-[190px]">
                  Staff
                </th>
                <th className="px-2 py-2 text-[11px] uppercase tracking-wider text-slate-500">Index</th>
                {data.catalogue.map((sk) => (
                  <th key={sk.id} className="px-2 py-2 align-bottom">
                    <div className="text-[10px] text-slate-600 whitespace-nowrap font-medium"
                      title={`${sk.group} · ${sk.name}`}
                      style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', height: 148 }}>
                      {sk.name}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.rows.map((r) => (
                <tr key={r.staff_id} className="hover:bg-slate-50" data-testid={`matrix-row-${r.staff_id}`}>
                  <td className="px-3 py-2 sticky left-0 bg-white z-10">
                    <Link to={`/admin/skills/staff/${r.staff_id}`} className="group">
                      <div className="font-medium text-slate-900 group-hover:text-teal-700 flex items-center gap-1">
                        {r.staff_name} <ChevronRight size={13} className="text-slate-400" />
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {r.staff_role || '—'}
                        {r.assessment_date
                          ? ` · ${r.assessment_date}${r.days_since !== null && r.days_since !== undefined ? ` (${r.days_since}d)` : ''}`
                          : ' · not assessed'}
                        {r.stale && (
                          <span className="ml-1 text-amber-700 font-medium" data-testid={`stale-${r.staff_id}`}>overdue</span>
                        )}
                      </div>
                    </Link>
                  </td>
                  <td className="px-2 py-2 text-center whitespace-nowrap">
                    <span className={`font-mono font-semibold ${indexTone(r.skill_index)}`} data-testid={`matrix-index-${r.staff_id}`}>
                      {r.skill_index === null ? '—' : `${r.skill_index}%`}
                    </span>
                    {r.delta !== null && r.delta !== undefined && (
                      <div className={`text-[10px] inline-flex items-center gap-0.5 ml-1 ${r.delta >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {r.delta >= 0 ? <TrendingUp size={10} /> : <TrendingDown size={10} />}{Math.abs(r.delta)}
                      </div>
                    )}
                  </td>
                  {data.catalogue.map((sk) => (
                    <td key={sk.id} className="px-2 py-2 text-center">
                      <LevelChip level={r.ratings[sk.id]} size="sm" testid={`cell-${r.staff_id}-${sk.id}`} />
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="bg-slate-50/70">
                <td className="px-3 py-2 text-[11px] uppercase tracking-wider text-slate-500 sticky left-0 bg-slate-50 z-10">
                  Trainers available
                </td>
                <td />
                {data.coverage.map((c) => (
                  <td key={c.skill_id} className="px-2 py-2 text-center text-xs font-semibold"
                    data-testid={`trainers-${c.skill_id}`}>
                    <span className={c.trainers ? 'text-emerald-700' : 'text-red-600'}>{c.trainers}</span>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SkillMatrixPage;
